import {Injectable, inject, signal} from "@angular/core";
import {AgentSaveDataInterface} from '@llm/utils/agents';
import {ClientService} from "@serv/client.service";
import {VehicleService} from "@serv/vehicle.service";
import {RoTypeService} from "@serv/ro-type.service";
import {OperationService} from "@serv/operation.service";
import {PlayMessageService} from '@llm/service/play_message.service';
import {DataSelectComponent} from '@llm/dialogs/data-select/data-select.component';
import {OsLlmService} from "@llm/service/os_register.service";
import {ApiOSPost} from '@class/mkg-os';
import {Router} from "@angular/router";
import {firstValueFrom, Observable, of, switchMap} from "rxjs";
import {map, timeout} from "rxjs/operators";
import {Client} from '@interface/client';
import {PropertiesDialogDataSelect} from "@llm/service/utils_main_comp_variables.service";
import {MatDialog} from "@angular/material/dialog";
import {Utiles_ferramentas} from '@llm/utils/utiles_ferramentas';
import {CpfCnpjValidatorsService, DocumentValidationsResults} from '@llm/utils/cpf_cnpj_validator.service';
import {DataService} from "@serv/data.service";
import {RoService} from '@serv/ro.service';
import {ToolsAiDialogsService} from '@llm/service/tools_ai-dialogs.service';
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";
import {Vehicle} from "@interface/vehicle";
import {API_ERRORS} from '@shared/lists/errors';
import {ToolResponse} from '@llm/models/schemas/agents/tools_utils';
import {LlmLoggingService} from '@llm/service/llm-logging.service';
import {NavigatorDeviceDetectorService} from "@llm/service/navigator_device_detector.service";

interface OsExtractedData {
  client: string,
  plate: string,
  prisma: string,
  report: string,
  tipo_operation: string,
  tipo_os: string,
}

const SPECIAL_FLAGS = {
    NEW_CLIENT_QUESTION: 'question_cadastro_novo_client',
    DEFAULT_CLIENT: 'defaultClient',
};

@Injectable({providedIn: 'root'})
export class RegisterNewOsTool {
  //Injectables
  private _routing = inject(Router);
  private _osService = inject(OsLlmService);
  private _globalOsService = inject(RoService);
  private _playMessageService = inject(PlayMessageService);
  private _clientService = inject(ClientService);
  private _vehicleService = inject(VehicleService);
  private _osTypeService = inject(RoTypeService);
  private _operationService = inject(OperationService);
  private _dialog = inject(MatDialog);
  private _utils = inject(Utiles_ferramentas);
  private _cpf_cnpj_ValidatorsService = inject(CpfCnpjValidatorsService);
  private _dataService = inject(DataService);
  private _toolsAiDialogsService = inject(ToolsAiDialogsService);
  private _console = inject(Tools_aiConsoleService);
  private _deviceDetector = inject(NavigatorDeviceDetectorService);
  private _llmLoggingService = inject(LlmLoggingService);

  //Signals
  private clientAssociationByPlate: string;
  private noteSystemClientAssociationByPlate: string;
  private service_order: ApiOSPost;

  private _bufferErrors = signal<string[]>([]);
  private _isOperationSuccessful = signal<boolean>(true);

  /**
   * Test
   *
   * ```json
   * {
   *   "extracted_data": {
   *     "cliente": "Richard Bonafin Queiroz",
   *     "plate": "OVS3736",
   *     "tipo_os": null,
   *     "tipo_operacion": "orçamento",
   *     "prisma": null,
   *     "relato_cliente": null
   *   }
   * }
   * ```
   * */

  //Funciones y Métodos del Servicio
  async processAgentData(response: AgentSaveDataInterface): Promise<ToolResponse> {
    this._llmLoggingService.info('Initialized procesing agent data: ', { response }, 'RegisterNewOsTool', 'processAgentData');
    try {
      let client: string;
      let clientTmp: string;
      let plate: string;
      let plateTmp: string;
      let feedbackAI: string[];

      this._bufferErrors.set([]);
      this._isOperationSuccessful.set(true);

      const { isBudget, ...dataOsExtracted } = response.extracted_data;
      const relevantData: OsExtractedData = dataOsExtracted;
      this._console.log('.... relevantData', relevantData)
      this.service_order = {} as ApiOSPost;

      await this.resolveVehicle(relevantData.plate)
      await this.resolveClient(relevantData.client)

      if (this.service_order.client == SPECIAL_FLAGS.NEW_CLIENT_QUESTION) {
        this.clientAssociationByPlate = null;
        const isCnpj = relevantData.client.length !== 11;
        const docType = isCnpj ? 'CNPJ' : 'CPF';
        return {
          status: 'pending',
            reason: `The registration was paused. Ask user: "Client with ${docType} ${relevantData.client} not found. Do you want to register them?". 
                     #feedbackInstruction: 
                     1. If YES ->To proceed with the customer registration, and then the service order registration will resume
                     2. If NO -> Continue OS registration using defaultClient in client field.`
          // reason: `The registration was paused to ask the user if they want to register the client with ${relevantData.client.length !== 11 ? 'CNPJ' + relevantData.client : 'CPF' + relevantData.client} #feedbackInstruction Instructions for handling this specific case: First of all, you should return the message asking the question. If the user's response is positive, the value extracted in feedback is cadastro_cliente, and in the voice message, it will be stated to the user that the service order registration will be paused to proceed with the client registration, and then the service order registration will resume. If the answer is negative, the client field will have the defaultClient value for the service order registration.`

        }
      }


      await this.resolveOsType(relevantData.tipo_os);
      await this.resolveOperation(relevantData.tipo_operation);
      this.service_order.prisma = relevantData.prisma || '';
      this.service_order.problem = relevantData.report || 'Atendimento Padrão';

      this._console.log('service_order: ', this.service_order)
      const data: ApiOSPost = this.service_order;
      this.clientAssociationByPlate = null;
      if (!this._isOperationSuccessful()) {
        return {
            status: this._isOperationSuccessful() ? 'ok':'error',
            reason: this._bufferErrors().join(', ').toString()}
      }
      if(data.client==await this._clientService.getDefaultId()) {
          data.clientTmp = relevantData.client;
      }
      const osId = await this._osService.add(data, isBudget);
      this._llmLoggingService.info(`Orden de Servicio creada con éxito. ID: ${osId.id}`, { osId: osId.id, data }, 'RegisterNewOsTool', 'processAgentData');
      //await this._playMessageService.playMessage('Cadastro de Ordem de Serviço feito com sucesso', true)
      if(this._deviceDetector.isMobile()){
          await this._routing.navigate([`/company/${this._dataService.company$.getValue().id}/mobile/os-list/${isBudget ? 'budget':'opened'}`]);
          this._llmLoggingService.info('Navegación completada tras creación de OS (mobile).', { path: `/company/${this._dataService.company$.getValue().id}/mobile/os-list/budget/` }, 'RegisterNewOsTool', 'processAgentData');
      } else {
          await this._routing.navigate([`/company/${this._dataService.company$.getValue().id}/os/${isBudget ? 'budget':'opened'}`]);
          this._llmLoggingService.info('Navegación completada tras creación de OS (desktop).', { path: `/company/${this._dataService.company$.getValue().id}/os/` }, 'RegisterNewOsTool', 'processAgentData');
      }

      const osCreated = await this._globalOsService.get(osId.id)
      this._console.log('osCreated: ', osCreated)
      const budget = isBudget ?'budget':'service order';
      const isClientDefault = (data.client==await this._clientService.getDefaultId())

      const clientName = data.clientTmp
        ? data.clientTmp
        : (await this._clientService.get(data.client)).name;

      const systemNote = this.noteSystemClientAssociationByPlate
        ? ` [SYSTEM_NOTE]: ${this.noteSystemClientAssociationByPlate}`
        : '';

      return {
          status: this._isOperationSuccessful() ? 'ok' : 'error',
          reason: this._isOperationSuccessful()
            ? `${budget} created successfully for plate ${relevantData.plate} and ${clientName}.${systemNote}`
            : this._bufferErrors().join(', ').toString()
      }

    } catch (error) {
      this._llmLoggingService.error(`Error procesando datos de la OS o al registrarla.`, error, { response }, 'RegisterNewOsTool', 'processAgentData');
      this._console.error("Error processing OS data:", error);
      return {
        status: 'error',
        reason: `Error ao procesar o cadastro da Ordem de Serviço: ${API_ERRORS[error.error.error]}. ###Intrucciones para manejar este caso: Primero de todo, debes devolver el mensaje de voz preguntando dando feedback al usuario para que sepa el porque del error y intercambiando mensajes con el podran determinar si el proceso se cancela o continua con alguna modificación. En la proxima salida de voice mensaje informará al usuario del error durante el cadastro,(#NOTA: required_more_info sera equivalente a true hasta tanto se determine que hacer)`
      };
    } finally {
        this.service_order = {} as ApiOSPost;
        this._isOperationSuccessful.update(() => true);
        this._bufferErrors.update(() => [])
    }
  }


  /**
   * Resuelve y obtiene el ID de un cliente a partir de una cadena de entrada.
   * La función es capaz de manejar dos tipos de entrada: un número de documento
   * (como CPF/CNPJ) o el nombre de un cliente.
   *
   * @description
   * El flujo de ejecución es el siguiente:
   * 1.  Primero, comprueba si la cadena `client` parece un documento numérico.
   *     - Si lo es, valida el documento.
   *       - Si no es válido, registra los errores, actualiza un estado interno de error y devuelve un ID de cliente por defecto.
   *       - Si es válido, busca al cliente por documento. Si lo encuentra, devuelve su ID; de lo contrario, devuelve el ID por defecto.
   *
   * 2.  Si la cadena no parece un documento, la trata como un nombre de cliente.
   *     - Busca en la base de datos todos los clientes que coincidan con ese nombre.
   *       - **Si no encuentra ninguno**, devuelve el ID de cliente por defecto.
   *       - **Si encuentra un único cliente**, devuelve su ID.
   *       - **Si encuentra múltiples clientes**, notifica al usuario mediante un mensaje de audio y
   *         abre un diálogo de selección para que el usuario elija el cliente correcto.
   *         La función resolverá con el ID del cliente seleccionado por el usuario.
   *
   * @async
   * @param {string} client La cadena de búsqueda, que puede ser un nombre o un número de documento.
   * @returns {Promise<string>} Una promesa que se resuelve con el ID del cliente encontrado.
   *          Este valor será el ID de un cliente por defecto si no se encuentra ninguna coincidencia,
   *          si ocurre un error, o si la validación del documento falla.
   *
   * @effects
   * - Puede modificar `_bufferErrors` y `_isOperationSuccessful` en caso de validación de documento fallida.
   * - Puede desencadenar interacciones con la interfaz de usuario (reproducir audio, abrir un diálogo)
   *   si se encuentran múltiples clientes con el mismo nombre.
   */
  private async resolveClient(client: string): Promise<void> {
    let isDoc: boolean = await this._utils.isNumericStringWithSpecialCharacters(client)
    let defaultClientId: string = await this._clientService.getDefaultId();
    let foundClient: string | Client[];
    let message: string = ''

    if (this.service_order.client) {
      await this._playMessageService.playMessage(`Á placa do veiculo tem um cliente associado, deseja prosseguir com ele?`, true)
      const subArrayToObj: PropertiesDialogDataSelect = {
        items: [
          {
            id: this.service_order.client,
            name: (await this._clientService.get(this.service_order.client)).name,
            descriptor: 'Cliente Associado'
          },
        ],
        icon: 'person',
        nameClass: 'cliente',
        references: {
          positive: 'ok',
          negative: 'não'
        }
      }
      let clientSelected: string = await firstValueFrom(this._toolsAiDialogsService.openDataSelectionDialog(subArrayToObj))
      if (this.service_order.client == clientSelected) {
          this.noteSystemClientAssociationByPlate= `[SYSTEM_NOTE] => The client provide by user is ${client} but, the user decided, through the system’s internal functions, to use a client ${(await this._clientService.get(this.service_order.client)).name} previously associated with the plate. `
        return;
      }
    }
    if (!client) {
      this.service_order.client = defaultClientId;
      return;
    }
    if (client == SPECIAL_FLAGS.DEFAULT_CLIENT) {
      this.service_order.client = defaultClientId;
      return;
    }

    if (await this._utils.isNumericStringWithSpecialCharacters(client)) {
        let validation: DocumentValidationsResults = await this._cpf_cnpj_ValidatorsService.validate(client);
        if (!validation.isValid) {
          this._bufferErrors.update(currentValue => [...currentValue, ...validation.errors]);
          this._isOperationSuccessful.set(false)
          this.service_order.client = defaultClientId;
          return;
        } else if (validation.isValid) {
          try {
            const cleanDoc = validation.doc.replace(/\D/g, '');
            this.service_order.client = (await this._clientService.getByDocument(cleanDoc))?.id;
            return;
          } catch (e) {
            this._console.error('Error:', e)
            this.service_order.client = SPECIAL_FLAGS.NEW_CLIENT_QUESTION;
            return;
          }
        }

    }else if(this.isValidObjectId(client)){
        // --- INICIO solve ID case ---
        try {
            const clientById = await this._clientService.get(client);
            if (clientById) {
                this.service_order.client = clientById.id;
                this.service_order.clientTmp = clientById.name;
                return;
            }
        } catch (error) {
            this._console.log('Input was not a valid client ID, proceeding to search by name.');
        }
        // --- FIN solve ID case ---
    } else {
      let foundClient: Client[] = await this.findClientInDb(client);
      if (!foundClient || foundClient.length == 0) {
        this.service_order.client = await this._clientService.getDefaultId();
        this.service_order.clientTmp = client;
        return;
      }
      if (!foundClient || foundClient.length == 1) {
            this.service_order.client = foundClient[0].id;
            this.service_order.clientTmp = foundClient[0].name;
            return;
      }
      if (foundClient.length >= 1) {
        await this._playMessageService.playMessage(`Encontramos vários clientes com o nome ${client}. Por favor, selecione o correto o feche janela para seleção padrão`, true)
        const subArrayToObj: PropertiesDialogDataSelect = {
          items: foundClient.map(dat => ({
            id: dat.id,
            name: dat.name,
            descriptor: dat.document,
          })),
          icon: 'person',
          nameClass: 'cliente',
          references: {
            negative: 'fechar janela'
          }
        }
        let selectedItem = await firstValueFrom(this._toolsAiDialogsService.openDataSelectionDialog(subArrayToObj))
        if (selectedItem === 'defaultItem') {
          this.service_order.client = defaultClientId;
          this.service_order.clientTmp = client;
          return;
        }
        this.service_order.client = selectedItem
        return;
      }
    }
    this.service_order.client = await this._clientService.getDefaultId();
    this.service_order.clientTmp = client;
    return;
  }


  private async resolveVehicle(plate: string): Promise<void> {
    if (!plate) {
      this.service_order.vehicle = await this._vehicleService.getDefaultId();
      return;
    }
    const cleanedPlate = plate.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (cleanedPlate.length !== 7) {
      this._bufferErrors.update(currentValue => [...currentValue, ...['']]);
      await this._playMessageService.playMessage(`Parece que não encontramos a placa ${cleanedPlate} em nossos registros.Usaremos a placa padrão`, false)
      this.service_order.vehicle = await this._vehicleService.getDefaultId();
      return;
    }
    try {
      const vehicle: Vehicle = await this._vehicleService.getByPlate(cleanedPlate);
      this.service_order.vehicle = vehicle.id
      //TODO:A modificar pues se le debe preguntar al usuario si desea usar ese mismo usuario como cliente
      this.service_order.client = vehicle?.client
      return;
    } catch (error) {
      this._llmLoggingService.error(`Error resolviendo vehículo con placa: ${cleanedPlate}`, error, { plate: cleanedPlate }, 'RegisterNewOsTool', 'resolveVehicle');
      this._console.error("Error resolving vehicle:", error);
      await this.registerNewVehicle(cleanedPlate);
    }
  }

  private async registerNewVehicle(plate: string): Promise<void> {
    try {
      let newVehicle: Vehicle = await this._vehicleService.getByPlateInfocar(plate);
      newVehicle.client = await this._clientService.getDefaultId();
      this._llmLoggingService.info(`Registrando nuevo vehículo: ${plate}`, { newVehicle }, 'RegisterNewOsTool', 'registerNewVehicle');
      this._console.log('a cadastrar vehicle: ', newVehicle)
      this.service_order.vehicle = await this._vehicleService.add(newVehicle);
      this._llmLoggingService.info(`Vehículo ${plate} registrado con éxito.`, { vehicleId: this.service_order.vehicle }, 'RegisterNewOsTool', 'registerNewVehicle');
      return;
    } catch (error) {
      this._llmLoggingService.error(`Error registrando nuevo vehículo con placa: ${plate}`, error, { plate }, 'RegisterNewOsTool', 'registerNewVehicle');
      this._console.error("Error registering new vehicle:", error);
      await this._playMessageService.playMessage(`Parece que não encontramos a placa ${plate} em nossos registros.Usaremos a placa padrão`, false)
      this.service_order.vehicle = await this._vehicleService.getDefaultId();
      this.service_order.vehicleTmp = plate;
      return;
    }
  }

  private async resolveOsType(osTypeId: string): Promise<void> {
    // Only active types may be assigned to a newly-created OS.
    const allRoTypes = await this._osTypeService.getAll({ skipInactives: true });
    this.service_order.type = osTypeId
      ? allRoTypes.find(t => t.type.toLowerCase() === osTypeId.toLowerCase())?.id ||
      allRoTypes.find(t => t.osTypeDefault)?.id!
      : allRoTypes.find(t => t.osTypeDefault)?.id!;
  }


  private async resolveOperation(operationType: string): Promise<void> {
    const allOperations = await this._operationService.getAll();
    this.service_order.operation = operationType
      ? allOperations.find(op => op.description.toLowerCase() === operationType.toLowerCase())?.id ||
      allOperations.find(op => op.opDefault)?.id!
      : allOperations.find(op => op.opDefault)?.id!;
  }

  //MICELANIAS

  /**
   * Busca un cliente en la base de datos por nombre.
   * @param name Nombre del cliente a buscar.
   * @returns Una Promesa que se resuelve a un array de `Client` si se encuentran, o un ID de cliente por defecto (string) si no.
   */
  private async findClientInDb(name: string): Promise<Client[]> {
      if (!name || name.trim() === '') {
          this._console.warn('Búsqueda de cliente con nombre vacío o inválido.');
          return [];
      }

      const searchParams = { search: name.trim() };

      try {
      const resp = await firstValueFrom(this._clientService.getPaginated(
        searchParams
      ).pipe(
        map(r => r.docs),
        timeout({first: 10000}),
      ));
      return resp ?? [];
    } catch (error: unknown) {
      const isErrorWithStatus = this.isHttpError(error);

      if (isErrorWithStatus && error.status === 400 && error.error?.error === 'PERSON_NOT_FOUND') {
          this._console.warn(`Cliente no encontrado para la búsqueda: "${name}". Devolviendo lista vacía.`);
          return [];
      }

      if (isErrorWithStatus && error.status === 404) {
          this._console.warn(`Cliente no encontrado (404) para la búsqueda: "${name}". Devolviendo lista vacía.`);
          return [];
      }

      this._llmLoggingService.error(
          `Error buscando cliente en DB por nombre: ${name}`,
          error,
          { name, searchParams },
          'RegisterNewOsTool',
          'findClientInDb'
      );

      return [];
    }
  }

  private isValidObjectId(id: string): boolean {
          return /^[0-9a-fA-F]{24}$/.test(id);
  }



  /**
   * Verifica si un error desconocido es un objeto de error HTTP con una propiedad 'status'.
   * Utilizado como type guard para validar la estructura de errores antes de acceder a propiedades específicas.
   * @param error El objeto de error a verificar, puede ser de cualquier tipo.
   * @returns true si el error es un objeto no nulo con una propiedad 'status', false en caso contrario.
   * Este método actúa como un type guard que estrecha el tipo de error a { status: number; error?: any }.
   */
  private isHttpError(error: unknown): error is { status: number; error?: any } {
    return typeof error === 'object' && error !== null && 'status' in error;
  }

  /**
   * Abre un diálogo para que el usuario seleccione un dato de una lista.
   * Utilizado cuando una búsqueda de datos devuelve múltiples resultados.
   * @param _data Array de objetos any para mostrar en el diálogo.
   * @returns Un Observable que emite el ID del dato seleccionado o el ID del dato por defecto.
   */
  openDataSelectionDialog(_data: PropertiesDialogDataSelect): Observable<string> {
    this._console.log("_data", _data);
    const dialogRef = this._dialog.open(DataSelectComponent, {
      width: '400px',
      data: _data
    });
    return dialogRef.afterClosed().pipe(
      switchMap(result => {
        if (result) {
          return of(result.toString());
        } else {
          return SPECIAL_FLAGS.DEFAULT_CLIENT;
        }
      })
    );
  }




}
